import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationExtras, ParamMap, Router } from "@angular/router";
import {
    CardSetTransformationService,
    CoreService,
    DataService, DialogResult,
    DialogService,
    FormConfigurationService, OptionSetService,
    SaveFormEvent,
    SpinnerService
} from "dspx-core";
import * as _ from 'lodash';
import { DateTime } from "luxon";
import { finalize, take } from "rxjs";
import { API_ENDPOINTS, API_ENDPOINT_KEYS } from "../../constants/api-endpoints.constants";
import {
    MISCELLANEOUS_TRANSACTION_TYPE_CODES,
    MinWaitForDialogToCloseMs,
    NAV_TAB_NUMBERS, OPTION_SETS, PAGE_INDEX,
    PAGE_NUMBERS,
    POP_UP_CODES,
    QUESTION_CODES,
    SAVE_RESULT_STATUS,
    SECTION_CODES, SERVER_ERROR_POP_UP_CODES
} from "../../constants/app-data.constants";
import { ROUTE_CONSTANTS } from "../../constants/route.constants";
import { TEMPLATES, VERSIONS } from "../../constants/templates.constants";
import { getDateRelativeToCurrentDate } from "../../helpers/date-helper";
import {
    CareAllowancePlacementAdjustmentSaveDataRequestInterface
} from "../../interfaces/care-allowance-placement-adjustment-save-data-request.interface";
import { AppDataService } from "../../services/app-data.service";
import { ErrorService } from "../../services/error.service";
import { ResponseParserService } from "../../services/response-parser.service";
import { BaseCardSetDetailComponent } from "../base-card-set-detail-component/base-card-set-detail.component";

@Component({
  selector: 'app-care-allowance-placement-adjustment',
  templateUrl: './care-allowance-placement-adjustment.component.html',
  styleUrl: './care-allowance-placement-adjustment.component.scss'
})
export class CareAllowancePlacementAdjustmentComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy {

  protected readonly fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;

  formServiceEndpoint: string = API_ENDPOINT_KEYS.getCareAllowancePlacementAdjustmentConfig;

  templateId: number = TEMPLATES.careAllowancePlacementAdjustment;
  versionNo: number = VERSIONS.latest;
  model: any;
  activeDialog: string = '';
  id: string = '';

  hasUnsavedAdjustments = false;
  hasAdjustmentLines = false;
  selectedTransactionType: string = '';
  totalFortnights: number = 0;
  immediateAdjustmentAmount: any;
  adjustmentFortnightDebitAmount: any;
  adjustmentFortnightCreditAmount: any;
  adjustmentTotalRecoveryAmount: any;
  adjustmentStartDate: any;
  adjustmentEndDate: any;
  adjustmentId: any;
  placementId: any;
  formIdentifier: string;
  viewMode: boolean = false;
  careAllowanceNegotiatedCreditAdjustmentAmountLimit: number;
  careAllowanceImmediatePaymentAdjustmentAmountLimit: number;
  careAllowanceNegotiatedDebitAdjustmentAmountLimit: number;
  careAllowanceOverPaymentRecoveryAdjustmentAmountLimit: number;

  private routePrefix = '';

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected appDataService: AppDataService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected changeDetector: ChangeDetectorRef,
    protected optionSetService: OptionSetService,
    private coreService: CoreService,
    private errorService: ErrorService,
    private spinnerService: SpinnerService,
    private dataService: DataService,
    private formConfigurationService: FormConfigurationService
  ) {
    super(router, appDataService, dialogService);
  }

  ngOnInit(): void {
    // We get the first part of the URL so that we can navigate back to that route after the Cancel or Save button is clicked.
    let index = this.router.url.lastIndexOf(`/${ROUTE_CONSTANTS.careAllowancePlacementAdjustment.prefix}`);
    this.routePrefix = this.router.url.substring(0, index);

    this.coreService.applicationFunctions = {
      continue: () => this.continue(),
      displayBtnSave: () => this.displayBtnSave() && !this.viewMode,
      displayBtnContinue: () => this.isTypeOverPayment() && !this.hasAdjustmentLines && !this.viewMode,
    };

    this.coreService.conditionFunctions = {
      alwaysReadOnly: () => true,
      isAdjustmentOverpayment: () => this.isAdjustmentOverpayment(),
      isAdjustmentDatesReadOnly: () => this.isTransactionType([MISCELLANEOUS_TRANSACTION_TYPE_CODES.immediatePayment, MISCELLANEOUS_TRANSACTION_TYPE_CODES.overPayment]) || this.viewMode,
      isAdjustmentOtherTypes: () => this.isAdjustmentOtherTypes(),
      hasAdjustmentLines: () => this.hasAdjustmentLines || this.viewMode,
      isTypeOverPayment: () => this.isTypeOverPayment() || this.hasAdjustmentLines,
      isTransactionType: ([...types]) => this.isTransactionType(types),
      isViewMode: () => this.viewMode
    };

    this.route.paramMap.subscribe((paramMap: ParamMap) => {
      const adjustmentId = paramMap.get('id');
      const totalLines = paramMap.get('totalLines');
      this.placementId = paramMap.get('placementId');
      if (!!totalLines) {
        if (this.router.url.includes(ROUTE_CONSTANTS.careAllowancePlacementAdjustment.generateAdjustmentLines)) {
          const recoveryAmount = paramMap.get('recoveryAmount');
          this.id = totalLines + '/' + recoveryAmount;
          this.formServiceEndpoint = API_ENDPOINT_KEYS.getCareAllowancePlacementAdjustmentConfigAndResponseWithGeneratedLines;
          this.hasUnsavedAdjustments = true;
          this.hasAdjustmentLines = true;
        }
      }
      if (!!adjustmentId) {
        this.id = adjustmentId;
        this.adjustmentId = adjustmentId;
        this.formServiceEndpoint = API_ENDPOINT_KEYS.getCareAllowancePlacementAdjustmentConfigAndResponse;
        this.viewMode = true;
      }
    });
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  formLoaded(event: any): void {
    if (this.hasUnsavedAdjustments){
      this.updateModelAfterGeneratedAdjustmentLines();
    }
    this.coreService.fileUploadFormIdentifiers[QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDocuments] = !!this.adjustmentId ? this.adjustmentId :  this.placementId;
    this.careAllowanceImmediatePaymentAdjustmentAmountLimit = event.careAllowanceImmediatePaymentAdjustmentAmountLimit;
    this.careAllowanceOverPaymentRecoveryAdjustmentAmountLimit = event.careAllowanceOverPaymentRecoveryAdjustmentAmountLimit;
    this.careAllowanceNegotiatedDebitAdjustmentAmountLimit = event.careAllowanceNegotiatedDebitAdjustmentAmountLimit;
    this.careAllowanceNegotiatedCreditAdjustmentAmountLimit = event.careAllowanceNegotiatedCreditAdjustmentAmountLimit;
  }

  modelChanged(event: any): void {
    this.selectedTransactionType = this.responseParserService.findQuestionModel(this.baseForm.model,
      NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      null,
      SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      null,
      QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentTransactionType
    );

    // Fetch the total number of fortnights from the model
    this.totalFortnights = this.responseParserService.findQuestionModel(this.baseForm.model,
      NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      null,
      SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      null,
      QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentNumberOfFortnights
    );

    // Fetch the immediate amount from the model
    this.immediateAdjustmentAmount = this.responseParserService.findQuestionModel(this.baseForm.model,
      NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      null,
      SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      null,
      QUESTION_CODES.careAllowancePlacementAdjustmentForm.immediateAdjustmentAmount
    );

    // Fetch the adjustment fortnight debit amount from the model
    this.adjustmentFortnightDebitAmount = this.responseParserService.findQuestionModel(this.baseForm.model,
      NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      null,
      SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      null,
      QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentFortnightDebitAmount
    );

    // Fetch the adjustment fortnight credit amount from the model
    this.adjustmentFortnightCreditAmount = this.responseParserService.findQuestionModel(this.baseForm.model,
      NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      null,
      SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      null,
      QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentFortnightCreditAmount
    );

    // Fetch the adjustment total recovery amount from the model
    this.adjustmentTotalRecoveryAmount = this.responseParserService.findQuestionModel(this.baseForm.model,
      NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      null,
      SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      null,
      QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentTotalRecoveryAmount
    );

    // Fetch the adjustment start date from the model
    this.adjustmentStartDate = this.responseParserService.findQuestionModel(this.baseForm.model,
      NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      null,
      SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      null,
      QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentStartDate
    );

    // Fetch the adjustment end date from the model
    this.adjustmentEndDate = this.responseParserService.findQuestionModel(this.baseForm.model,
      NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      null,
      SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      null,
      QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentEndDate
    );

    if(this.selectedTransactionType === MISCELLANEOUS_TRANSACTION_TYPE_CODES.immediatePayment) {
      _.set(
        this.baseForm.model,
        `form.tabs.tab${NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails}` +
        `.page${PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails}` +
        `.${SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails}` +
        `.${QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentStartDate}`,
        this.getCurrentDateString()
      );

      _.set(
        this.baseForm.model,
        `form.tabs.tab${NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails}` +
        `.page${PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails}` +
        `.${SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails}` +
        `.${QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentEndDate}`,
        this.getCurrentDateString()
      );

      this.responseParserService.updateModel(
        this.baseForm,
        NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
        PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
        PAGE_INDEX.noRepeater,
        SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails,
        {
          [QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentStartDate]: this.getCurrentDateString(),
          [QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentEndDate]: this.getCurrentDateString()
        }
      );
    }
  }

  filter(event: any): void {
  }

  buttonClicked(event: any): void {
  }

  save(event: SaveFormEvent) {
    if (!event.isFormValid) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showDialog(POP_UP_CODES.common.validationError);
      } else {
        this.validateFormAndSave(event);
      }
    } else {
      this.validateFormAndSave(event);
    }
  }

  cancel(event: any) {
    this.exit();
  }

  actionButton_adjustmentLineDetailsGrid_datagrid_edit_Clicked(event: any) {
    this.activeDialog = POP_UP_CODES.careAllowancePlacementAdjustment.editAdjustmentLineAmount;
    this.dialogService.showDialog(this.activeDialog, event.event.data);
  }

  dialogButton_saveSuccessful_Clicked(event: any) {
    this.exit();
  }

  dialogButton_editAdjustmentLineAmount_Clicked(event: any) {
    let gridModel = this.baseForm.model.form.tabs.tab2.page1.adjustmentLineDetailsSection.adjustmentLineDetailsGrid;
    gridModel.splice(event.lineNumber - 1, 1, { ...gridModel[event.lineNumber - 1], amount: event.lineAmount });
    this.baseForm.model.form.tabs.tab2.page1.adjustmentLineDetailsSection.adjustmentLineDetailsGrid = [...gridModel];
    this.model = { ...this.baseForm.model };
    this.changeDetector.detectChanges();
  }

  private exit(ignoreUnsavedChanges = false): void {
    const navExtras = this.getNavigationExtras();
    this.router.navigate([this.routePrefix], navExtras);
  }

  private getNavigationExtras(): NavigationExtras {
    return {
      state: {
        placementFilterRequest: history.state.placementFilterRequest,
        personFilterRequest: history.state.personFilterRequest,
      }
    };
  }

  private isAdjustmentOverpayment() {
    return false;
  }

  private isAdjustmentDatesReadOnly() {
    return false;
  }

  private isAdjustmentOtherTypes() {
    return false;
  }

  private continue() {
    const { careAllowanceAdjustmentSetting, adjustmentAmount } = this.getAdjustmentSettings();
    if (!!careAllowanceAdjustmentSetting) {
      if (this.checkConfigurationAmount(careAllowanceAdjustmentSetting, adjustmentAmount)) {
        this.dialogService.showConfirm(this.activeDialog, { configuredAmount: careAllowanceAdjustmentSetting.toString() })
          .pipe(take(1))
          .subscribe(result => {
            if (result === DialogResult.ACCEPTED){
              this.navigateToGenerateAdjustmentLines();
            }
            else{
              this.resetAmountFields();
            }
          });
      }
      else {
        this.navigateToGenerateAdjustmentLines();
      }
    }
  }

  private navigateToGenerateAdjustmentLines() {
    this.appDataService.setAppData('adjustmentSelectedTransactionType', this.selectedTransactionType);
    this.appDataService.setAppData('adjustmentTotalFortnights', this.totalFortnights);
    this.appDataService.setAppData('adjustmentTotalRecoveryAmount', this.adjustmentTotalRecoveryAmount);
    const navExtras = this.getNavigationExtras();
    this.router.navigate([this.router.url, this.totalFortnights, ROUTE_CONSTANTS.careAllowancePlacementAdjustment.generateAdjustmentLines, this.adjustmentTotalRecoveryAmount], navExtras);
  }

  private displayBtnSave() {
    return (this.selectedTransactionType !== MISCELLANEOUS_TRANSACTION_TYPE_CODES.overPayment)
      ||
      (this.selectedTransactionType === MISCELLANEOUS_TRANSACTION_TYPE_CODES.overPayment && this.hasUnsavedAdjustments);
  }

  private isTypeOverPayment() {
    return this.selectedTransactionType === MISCELLANEOUS_TRANSACTION_TYPE_CODES.overPayment;
  }

  private isTransactionType(types) {
    if (!this.baseForm?.model?.form?.tabs) return false;

    const transactionType = this.formConfigurationService.getQuestionResponse(
      this.baseForm.model.form.tabs,
      'tab' + NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      'page' + PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      -1,
      SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails,
      -1,
      QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentTransactionType
    );

    this.selectedTransactionType = transactionType;
    return types.includes(transactionType);
  }

  private updateModelAfterGeneratedAdjustmentLines() {
    _.set(
      this.baseForm.model,
      `form.tabs.tab${NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails}` +
      `.page${PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails}` +
      `.${SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails}` +
      `.${QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentTransactionType}`,
      this.appDataService.getAppData('adjustmentSelectedTransactionType')
    );

    _.set(
      this.baseForm.model,
      `form.tabs.tab${NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails}` +
      `.page${PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails}` +
      `.${SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails}` +
      `.${QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentNumberOfFortnights}`,
      this.appDataService.getAppData('adjustmentTotalFortnights')
    );

    _.set(
      this.baseForm.model,
      `form.tabs.tab${NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails}` +
      `.page${PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails}` +
      `.${SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails}` +
      `.${QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentTotalRecoveryAmount}`,
      this.appDataService.getAppData('adjustmentTotalRecoveryAmount')
    );

    this.adjustmentTotalRecoveryAmount = this.appDataService.getAppData('adjustmentTotalRecoveryAmount');
  }

  private validateFormAndSave(event: SaveFormEvent) {

    if (this.selectedTransactionType !== MISCELLANEOUS_TRANSACTION_TYPE_CODES.overPayment) {
      const { careAllowanceAdjustmentSetting, adjustmentAmount } = this.getAdjustmentSettings();

      if (!!careAllowanceAdjustmentSetting) {
        if (this.checkConfigurationAmount(careAllowanceAdjustmentSetting, adjustmentAmount)) {
          this.dialogService.showConfirm(this.activeDialog, { configuredAmount: careAllowanceAdjustmentSetting.toString() })
            .pipe(take(1))
            .subscribe(result => {
              if (result === DialogResult.ACCEPTED){
                this.validateAndSaveData(event);
              }
              else {
                this.resetAmountFields();
              }
            });
        }
        else {
          this.validateAndSaveData(event);
        }
      }
    }
    else {
      this.validateAndSaveData(event);
    }

  }

  private validateAndSaveData(event: SaveFormEvent) {
    const saveDataRequest = this.createSaveDataRequest(event);

    switch (this.selectedTransactionType) {
      case MISCELLANEOUS_TRANSACTION_TYPE_CODES.debitAdjustment:
      case MISCELLANEOUS_TRANSACTION_TYPE_CODES.creditAdjustment:
        this.checkStartDate(saveDataRequest, event);
        break;
      default:
        this.showConfirmDialogAndSave(saveDataRequest);
        break;
    }
  }

  private createSaveDataRequest(event: SaveFormEvent): CareAllowancePlacementAdjustmentSaveDataRequestInterface {
    return {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: !!this.adjustmentId ? this.id : null,
      response: event.model,
      expectedLastUpdateTime: event.lastUpdatedTime,
      isSubmit: event.isSubmit,
      placementId: this.placementId,
      saveGeneratedAdjustmentLines: this.hasUnsavedAdjustments
    };
  }

  private checkStartDate(saveDataRequest: CareAllowancePlacementAdjustmentSaveDataRequestInterface, event: SaveFormEvent) {
    const startDate = DateTime.fromFormat(this.adjustmentStartDate, 'dd/MM/yyyy', { locale: 'en-AU' });
    if (startDate < getDateRelativeToCurrentDate('day', -30)) {
      setTimeout(() => {
        this.activeDialog = POP_UP_CODES.careAllowancePlacementAdjustment.startDateLessThanAMonthInPast;
        this.dialogService.showConfirm(this.activeDialog)
          .pipe(take(1))
          .subscribe(result => {
            if (result === DialogResult.ACCEPTED){
              this.checkEndDate(saveDataRequest, event);
            }
          });
      }, MinWaitForDialogToCloseMs);

    }
    else {
      this.checkEndDate(saveDataRequest, event);
    }
  }

  private checkEndDate(saveDataRequest: CareAllowancePlacementAdjustmentSaveDataRequestInterface, event: SaveFormEvent) {
    const endDate = DateTime.fromFormat(this.adjustmentEndDate, 'dd/MM/yyyy', { locale: 'en-AU' });
    if (endDate > getDateRelativeToCurrentDate('day', 30)) {
      setTimeout(() => {
        this.activeDialog = POP_UP_CODES.careAllowancePlacementAdjustment.endDateGreaterThanAMonthInFuture;
        this.dialogService.showConfirm(this.activeDialog)
          .pipe(take(1))
          .subscribe(result => {
            if (result === DialogResult.ACCEPTED){
              this.showConfirmDialogAndSave(saveDataRequest);
            }
          });
      }, MinWaitForDialogToCloseMs);
    }
    else {
      this.showConfirmDialogAndSave(saveDataRequest);
    }
  }

  private showConfirmDialogAndSave(saveDataRequest: CareAllowancePlacementAdjustmentSaveDataRequestInterface) {
    const selectedTransactionTypeText = this.optionSetService.getOptionText(OPTION_SETS.transactionType.code.toLowerCase(), this.selectedTransactionType).split('-')[1].trim();
    const confirmPrefix =
      this.selectedTransactionType === MISCELLANEOUS_TRANSACTION_TYPE_CODES.immediatePayment
      ||
      this.selectedTransactionType == MISCELLANEOUS_TRANSACTION_TYPE_CODES.overPayment
        ? 'An'
        : 'A';
    this.activeDialog = POP_UP_CODES.careAllowancePlacementAdjustment.confirmSaveAdjustment;
    setTimeout(() => {
      this.dialogService.showConfirm(this.activeDialog, { confirmPrefix: confirmPrefix, adjustmentType: selectedTransactionTypeText })
        .pipe(take(1))
        .subscribe(result => {
          if (result === DialogResult.ACCEPTED){
            this.saveData(saveDataRequest);
          }
        });
    }, MinWaitForDialogToCloseMs);
  }

  private saveData(saveDataRequest: CareAllowancePlacementAdjustmentSaveDataRequestInterface) {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.dataService.saveData(API_ENDPOINTS.careAllowancePlacementAdjustment.save, saveDataRequest)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(data => {
          if (data?.status === SAVE_RESULT_STATUS.success) {
            if(SERVER_ERROR_POP_UP_CODES.saveCareAllowancePlacementAdjustment.includes(data.message)) {
              this.activeDialog = data.message;
              this.dialogService.showDialog(this.activeDialog);
            }
            else {
              this.id = data.id;
              this.activeDialog = POP_UP_CODES.common.saveSuccessful;
              this.dialogService.showDialog(this.activeDialog);
            }
          }
        })
    );
  }

  private getCurrentDateString() {
    const now = new Date(Date.now());

    const month = (now.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-based, so add 1
    const day = now.getDate().toString().padStart(2, '0');
    const year = now.getFullYear().toString();
    return `${day}/${month}/${year}`;
  }

  private resetAmountFields() {
    const path = `form.tabs.tab${NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails}.page${PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails}.${SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails}`;
    switch (this.selectedTransactionType) {
      case MISCELLANEOUS_TRANSACTION_TYPE_CODES.immediatePayment:
        this.responseParserService.updateModel(
          this.baseForm,
          NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
          PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
          PAGE_INDEX.noRepeater,
          SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails,
          {
            [QUESTION_CODES.careAllowancePlacementAdjustmentForm.immediateAdjustmentAmount]: null
          }
        );
        break;
      case MISCELLANEOUS_TRANSACTION_TYPE_CODES.overPayment:
        this.responseParserService.updateModel(
          this.baseForm,
          NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
          PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
          PAGE_INDEX.noRepeater,
          SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails,
          {
            [QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentTotalRecoveryAmount]: null
          }
        );
        break;
      case MISCELLANEOUS_TRANSACTION_TYPE_CODES.debitAdjustment:
        this.responseParserService.updateModel(
          this.baseForm,
          NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
          PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
          PAGE_INDEX.noRepeater,
          SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails,
          {
            [QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentFortnightDebitAmount]: null
          }
        );
        break;
      case MISCELLANEOUS_TRANSACTION_TYPE_CODES.creditAdjustment:
        this.responseParserService.updateModel(
          this.baseForm,
          NAV_TAB_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
          PAGE_NUMBERS.careAllowancePlacementAdjustmentForm.adjustmentDetails,
          PAGE_INDEX.noRepeater,
          SECTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentDetails,
          {
            [QUESTION_CODES.careAllowancePlacementAdjustmentForm.adjustmentFortnightCreditAmount]: null
          }
        );
        break;
    }
    this.changeDetector.detectChanges();
  }

  private getAdjustmentSettings() {
    let careAllowanceAdjustmentSetting: number;
    let adjustmentAmount: number;
    switch (this.selectedTransactionType) {
      case MISCELLANEOUS_TRANSACTION_TYPE_CODES.immediatePayment:
        careAllowanceAdjustmentSetting = this.careAllowanceImmediatePaymentAdjustmentAmountLimit;
        adjustmentAmount = this.immediateAdjustmentAmount;
        break;
      case MISCELLANEOUS_TRANSACTION_TYPE_CODES.overPayment:
        careAllowanceAdjustmentSetting = this.careAllowanceOverPaymentRecoveryAdjustmentAmountLimit;
        adjustmentAmount = this.adjustmentTotalRecoveryAmount;
        break;
      case MISCELLANEOUS_TRANSACTION_TYPE_CODES.debitAdjustment:
        careAllowanceAdjustmentSetting = this.careAllowanceNegotiatedDebitAdjustmentAmountLimit;
        adjustmentAmount = this.adjustmentFortnightDebitAmount;
        break;
      case MISCELLANEOUS_TRANSACTION_TYPE_CODES.creditAdjustment:
        careAllowanceAdjustmentSetting = this.careAllowanceNegotiatedCreditAdjustmentAmountLimit;
        adjustmentAmount = this.adjustmentFortnightCreditAmount;
        break;
      default:
        break;
    }

    return { careAllowanceAdjustmentSetting, adjustmentAmount };
  }

  private checkConfigurationAmount(settingValue: number, adjustmentAmount: number) {
    let checkConfigurationAmount = false;
    switch (this.selectedTransactionType) {
      case MISCELLANEOUS_TRANSACTION_TYPE_CODES.immediatePayment:
      case MISCELLANEOUS_TRANSACTION_TYPE_CODES.debitAdjustment:
        if (adjustmentAmount > settingValue){
          this.activeDialog = POP_UP_CODES.careAllowancePlacementAdjustment.greaterThanConfiguredAmount;
          checkConfigurationAmount = true;
        }
        break;
      case MISCELLANEOUS_TRANSACTION_TYPE_CODES.overPayment:
      case MISCELLANEOUS_TRANSACTION_TYPE_CODES.creditAdjustment:
        if (adjustmentAmount < settingValue){
          this.activeDialog = POP_UP_CODES.careAllowancePlacementAdjustment.lessThanConfiguredAmount;
          checkConfigurationAmount = true;
        }
        break;
      default:
        break;
    }
    return checkConfigurationAmount;
  }
}
